import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PodWorkspaceCard } from "@/features/podWorkspace/components/podWorkspaceCard.component";
import { PodWorkspaceService } from "@/lib/podWorkspace/podWorkspace.service";
import type { RecentlyPlayedWorkspace } from "@/lib/podWorkspace/podWorkspace.service";
import type { Podcast } from "@/lib/podcast/podcast.service";
import { useTranslation } from "react-i18next";
import { usePodWorkspaceStore } from "@/lib/podWorkspace/podWorkspace.store";

interface RecentlyPlayedSectionProps {
	regeneratingPodcasts: Set<string>;
	currentlyPlayingPodcastId: string | null;
	isPlayerPaused: boolean;
	onPlayPodcast: (podcastId: string) => void;
	onRegenerateAudio: (podcastId: string) => void;
	onOpenChat: (workspaceId: string) => void;
	onRetryPodcast: (podcastId: string) => void;
	onWorkspaceDeleted?: () => void;
}

/**
 * This is the recent section component.
 * It displays recently active workspaces including both:
 * - Recently played podcasts (based on listen activity)
 * - Newly created podcasts that haven't been played yet
 * Shows the most recent podcast activity in a horizontal scrolling grid.
 */
export const RecentlyPlayedSection = ({
	regeneratingPodcasts,
	currentlyPlayingPodcastId,
	isPlayerPaused,
	onPlayPodcast,
	onRegenerateAudio,
	onOpenChat,
	onRetryPodcast,
	onWorkspaceDeleted,
}: RecentlyPlayedSectionProps) => {
	const { t } = useTranslation();
	const [recentWorkspaces, setRecentWorkspaces] = useState<
		RecentlyPlayedWorkspace[]
	>([]);
	const [podcastsByWorkspace, setPodcastsByWorkspace] = useState<
		Record<string, Podcast>
	>({});
	const [isLoading, setIsLoading] = useState(true);

	// Subscribe to store updates - merge fresh podcast data without refetching
	const storePodcastsByWorkspace = usePodWorkspaceStore(
		(state) => state.podcastsByWorkspace,
	);

	// Fetch recently played workspaces once on mount
	useEffect(() => {
		const fetchRecentlyPlayed = async () => {
			try {
				setIsLoading(true);
				const workspaces =
					await PodWorkspaceService.getRecentlyPlayedWorkspaces(10);
				setRecentWorkspaces(workspaces);

				// Use the enriched podcast data that comes from the backend
				// The backend already includes progress information for each podcast
				const podcastsMap: Record<string, Podcast> = {};
				workspaces.forEach((workspace) => {
					// Prioritize: COMPLETED > PROCESSING > any other status
					const firstPodcast =
						workspace.podcasts.find((p) => p.status === "COMPLETED") ||
						workspace.podcasts.find((p) => p.status === "PROCESSING") ||
						workspace.podcasts[0];
					if (firstPodcast) {
						podcastsMap[workspace.id] = firstPodcast as unknown as Podcast; // this is a workaround to fix the type error TODO: fix this
					}
				});
				setPodcastsByWorkspace(podcastsMap);
			} catch (error) {
				console.error("Failed to fetch recently played workspaces:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchRecentlyPlayed();
	}, []);

	// Merge store's fresh podcast data (from polling) into local state
	// This updates podcast statuses without refetching the entire list
	useEffect(() => {
		// Only update if there are podcasts in local state and store has updates
		if (Object.keys(podcastsByWorkspace).length === 0) return;

		setPodcastsByWorkspace((prev) => {
			let hasChanges = false;
			const updated = { ...prev };

			// Update podcasts from store if they exist in our local state
			Object.keys(storePodcastsByWorkspace).forEach((workspaceId) => {
				const storePodcast = storePodcastsByWorkspace[workspaceId];
				const currentPodcast = prev[workspaceId];

				if (storePodcast && currentPodcast) {
					// Only update if status or id changed to avoid unnecessary re-renders
					if (
						currentPodcast.status !== storePodcast.status ||
						currentPodcast.id !== storePodcast.id
					) {
						updated[workspaceId] = storePodcast;
						hasChanges = true;
					}
				}
			});

			// Only return new object if there were actual changes
			return hasChanges ? updated : prev;
		});
	}, [storePodcastsByWorkspace, podcastsByWorkspace]);

	if (isLoading) {
		return (
			<div className="mb-4 md:mb-8">
				<div className="mb-3 md:mb-4 flex items-center justify-between px-1">
					<h2 className="text-xs text-muted-foreground md:text-foreground md:text-2xl">
						<div className="flex items-center gap-2">
							<Clock className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
							{t("dashboard.recentlyPlayed.title")}
						</div>
					</h2>
				</div>
				<Card className="flex h-40 md:h-48 w-full md:w-72 md:shrink-0 items-center justify-center border bg-card/50 backdrop-blur-sm">
					<div className="flex items-center gap-2 text-muted-foreground">
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
						<span className="text-sm">
							{t("dashboard.recentlyPlayed.loading")}
						</span>
					</div>
				</Card>
			</div>
		);
	}

	if (recentWorkspaces.length === 0) {
		return null; // Don't show section if no recently played workspaces
	}

	return (
		<div className="mb-4 md:mb-8">
			<div className="mb-3 md:mb-4 flex items-center justify-between px-1">
				<h2 className="text-xs text-muted-foreground md:text-foreground md:text-2xl">
					<div className="flex items-center gap-2">
						<Clock className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
						{t("dashboard.recentlyPlayed.title")}
					</div>
				</h2>
				<Link
					to="/podspace"
					className="hidden md:flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1 px-1 -mr-1"
				>
					{t("common.viewAll")}
					<ArrowRight className="h-4 w-4" />
				</Link>
			</div>

			{/* Desktop: Show all in horizontal scroll */}
			<div className="hidden md:flex gap-4 overflow-x-auto pb-4 pt-3 px-1">
				{recentWorkspaces.map((ws) => {
					const podcast = podcastsByWorkspace[ws.id];
					const isCurrentlyPlaying = podcast?.id === currentlyPlayingPodcastId;

					return (
						<PodWorkspaceCard
							key={ws.id}
							workspace={ws}
							podcast={podcast}
							isRegenerating={regeneratingPodcasts.has(podcast?.id || "")}
							isCurrentlyPlaying={isCurrentlyPlaying}
							isPlayerPaused={isPlayerPaused}
							onPlayPodcast={onPlayPodcast}
							onRegenerateAudio={onRegenerateAudio}
							onOpenChat={onOpenChat}
							onRetryPodcast={onRetryPodcast}
						/>
					);
				})}
			</div>

			{/* Mobile: Vertical stack, show up to 3 + "More" button */}
			<div className="md:hidden flex flex-col gap-2.5">
				{recentWorkspaces.slice(0, 3).map((ws) => {
					const podcast = podcastsByWorkspace[ws.id];
					const isCurrentlyPlaying = podcast?.id === currentlyPlayingPodcastId;

					return (
						<PodWorkspaceCard
							key={ws.id}
							workspace={ws}
							podcast={podcast}
							isRegenerating={regeneratingPodcasts.has(podcast?.id || "")}
							isCurrentlyPlaying={isCurrentlyPlaying}
							isPlayerPaused={isPlayerPaused}
							onPlayPodcast={onPlayPodcast}
							onRegenerateAudio={onRegenerateAudio}
							onOpenChat={onOpenChat}
							onRetryPodcast={onRetryPodcast}
							onWorkspaceDeleted={onWorkspaceDeleted}
							menuVariant="bottomSheet"
						/>
					);
				})}
				{recentWorkspaces.length > 3 && (
					<Link to="/podspace" className="w-full">
						<Button
							variant="ghost"
							className="w-full text-sm text-muted-foreground hover:text-foreground gap-1.5"
						>
							{t("common.viewAll")}
							<ArrowRight className="h-4 w-4" />
						</Button>
					</Link>
				)}
			</div>
		</div>
	);
};
